/** FARM & RANCH WELDING PLANS™ — all customer-facing content and asset mapping. */

export interface PlanImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface ProjectPage extends PlanImage {
  caption: string
}

export interface TechProof extends PlanImage {
  title: string
  project: string
  description: string
}

export interface Volume extends PlanImage {
  number: string
  title: string
  projects: string
  description: string
  /** Every project in the volume, as printed in the PDF index. */
  projectList: string[]
}

export interface Bonus extends PlanImage {
  number: string
  title: string
  description: string
}

export type SnapshotIcon = 'projects' | 'volumes' | 'dimensions' | 'bonus'

export interface SnapshotItem {
  label: string
  icon: SnapshotIcon
}

export interface Faq {
  question: string
  answer: string
}

/** Image host: light WebP files in the store's Impultienda resources. */
export const IMAGE_ORIGIN = 'https://aekpgsbqjzqntlbucbts.supabase.co'
const IMG = `${IMAGE_ORIGIN}/storage/v1/object/public/store-resources/bc08bdc9-f180-495d-a689-85c5860a2f08/`

export const PRODUCT = {
  name: 'FARM & RANCH WELDING PLANS™',
  wordmarkA: 'Farm & Ranch',
  wordmarkB: 'Welding Plans',
  descriptor: 'Farm & Ranch Metal Project Plans to Build & Sell',
} as const

export const COMMERCIAL_RIGHTS = {
  label: 'COMMERCIAL BUILD RIGHTS INCLUDED',
  sentence: 'Build it for your own place — or build and sell the finished pieces to farms and ranches near you.',
} as const

export const OFFER_ANCHOR = '#offer'
export const PREVIEW_ANCHOR = '#project-preview'

export const TICKER_ITEMS: string[] = [
  '30 FARM & RANCH PROJECTS',
  '6 DIGITAL VOLUMES',
  'DIMENSIONS & CUT LISTS',
  'BUILD & SELL CARDS',
  '5 FREE BONUS RESOURCES',
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Preview', href: '#project-preview' },
  { label: 'Inside the Plans', href: '#technical-proof' },
  { label: 'Volumes', href: '#volumes' },
  { label: 'Bonuses', href: '#bonuses' },
  { label: 'FAQ', href: '#faq' },
]

/** All plan pages are vertical 4:5. */
const PAGE_SIZE = { width: 1100, height: 1376 }

const COVERS = {
  v1: `${IMG}1792795719711_fr-vol-1-62f21444.webp`,
  v2: `${IMG}1792795779122_fr-vol-2-bf3210bd.webp`,
  v3: `${IMG}1792795729798_fr-vol-3-ee753402.webp`,
  v4: `${IMG}1792795737002_fr-vol-4-87ea222a.webp`,
  v5: `${IMG}1792795743913_fr-vol-5-136f1c6c.webp`,
  v6: `${IMG}1792795749726_fr-vol-6-cc29c16a.webp`,
}

export const HERO_MOCKUP: PlanImage = {
  src: `${IMG}1792795714464_fr-hero-mockup-2a9d61cd.webp`,
  alt: 'The six FARM & RANCH WELDING PLANS™ volume covers: gates and fence hardware, hay and livestock feeders, pens and panels, water and hay handling, ATV and yard utility, barn and stable builds',
  width: 1100,
  height: 1375,
}

export const SNAPSHOT: SnapshotItem[] = [
  { label: '30 Farm & Ranch Projects', icon: 'projects' },
  { label: '6 Organized Volumes', icon: 'volumes' },
  { label: 'Dimensions + Cut Lists', icon: 'dimensions' },
  { label: '5 Free Bonus Resources', icon: 'bonus' },
]

/** Project P01, Volume 01, PDF pages 06–11 — the complete project, in order. */
export const PREVIEW_PROJECT = {
  code: 'P01',
  name: '12 FT Tube Field Gate',
  volume: 'Volume 01',
} as const

export const PROJECT_PAGES: ProjectPage[] = [
  {
    src: `${IMG}1792795784923_fr-p01-01-finished-965e75f9.webp`,
    caption: 'The finished build',
    alt: 'Volume 01, page 06: P01 12 FT Tube Field Gate finished build — 144 in / 3657.6 mm wide, 50 in / 1270 mm high',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792795791709_fr-p01-02-plan-28aab380.webp`,
    caption: '3-view dimensioned plan — inches & millimeters',
    alt: 'Volume 01, page 07: P01 front, side and top views with dimensions in inches and millimeters',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792795796882_fr-p01-03-cutlist-4cc0ba4b.webp`,
    caption: 'Exploded view & complete cut list',
    alt: 'Volume 01, page 08: P01 exploded view and cut list for parts A to F in inches and millimeters',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792795802046_fr-p01-04-steps-7a45aeae.webp`,
    caption: 'Step-by-step fabrication',
    alt: 'Volume 01, page 09: P01 eight-step illustrated fabrication sequence',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792795807206_fr-p01-05-welds-88ec612e.webp`,
    caption: 'Weld map & fabrication details',
    alt: 'Volume 01, page 10: P01 weld map with seam locations, tack order and finish checklist',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792795812660_fr-p01-06-sell-card-6383bc83.webp`,
    caption: 'Build & Sell card',
    alt: 'Volume 01, page 11: P01 Build & Sell card with finished size, materials and maker notes',
    ...PAGE_SIZE,
  },
]

export const PLAN_BENEFITS: string[] = ['Cut the steel.', 'Weld it square.', 'Set it on the farm — or sell it.']

export const TECH_PROOF: TechProof[] = [
  {
    src: `${IMG}1792795819261_fr-tech-01-p02-plan-deb98b43.webp`,
    title: 'KNOW THE DIMENSIONS',
    project: 'Project 02 · 4 FT Walk-Through Gate · Volume 01',
    description: 'Every view dimensioned before you cut a single part.',
    alt: 'Volume 01: P02 4 FT Walk-Through Gate three-view dimensioned plan',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792795824690_fr-tech-02-p03-cutlist-f3dca525.webp`,
    title: 'KNOW WHAT TO CUT',
    project: 'Project 03 · Pipe Corral Gate with Slide Latch · Volume 01',
    description: 'Parts, quantities, stock and exact cut sizes in one table.',
    alt: 'Volume 01: P03 Pipe Corral Gate exploded view and complete cut list',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792795829891_fr-tech-03-p04-welds-66587f36.webp`,
    title: 'KNOW WHERE TO WELD',
    project: 'Project 04 · Pipe H-Brace Corner Assembly · Volume 01',
    description: 'Weld map, tack order and fit-up details for every joint.',
    alt: 'Volume 01: P04 Pipe H-Brace Corner weld map and fabrication details',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792795835050_fr-tech-04-p05-steps-a9184058.webp`,
    title: 'FOLLOW THE BUILD',
    project: 'Project 05 · Gate Hardware Set · Volume 01',
    description: 'An illustrated sequence from cut parts to the finished piece.',
    alt: 'Volume 01: P05 Gate Hardware Set step-by-step fabrication',
    ...PAGE_SIZE,
  },
]

const COVER_SIZE = { width: 1100, height: 1376 }

export const VOLUMES: Volume[] = [
  {
    number: 'VOLUME 01',
    title: 'Farm Gates & Fence Hardware',
    projects: '5 projects',
    description: 'Field gates, corral gates, fence corners and the hardware to hang them.',
    projectList: [
      'P01 — 12 FT Tube Field Gate',
      'P02 — 4 FT Walk-Through Gate',
      'P03 — Pipe Corral Gate with Slide Latch',
      'P04 — Pipe H-Brace Corner Assembly',
      'P05 — Gate Hardware Set',
    ],
    src: COVERS.v1,
    alt: 'Volume 01 cover: Farm Gates & Fence Hardware',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 02',
    title: 'Hay & Livestock Feeders',
    projects: '5 projects',
    description: 'Round bale feeders, hay racks, goat feeders and feed troughs.',
    projectList: [
      'P06 — Six-Panel Round Bale Feeder',
      'P07 — Wall-Mount Square Bale Hay Rack',
      'P08 — Keyhole Feeder for Goats & Sheep',
      'P09 — Mineral & Lick Tub Holder',
      'P10 — Steel Feed Bunk Trough',
    ],
    src: COVERS.v2,
    alt: 'Volume 02 cover: Hay & Livestock Feeders',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 03',
    title: 'Pens, Panels & Small-Stock Handling',
    projects: '5 projects',
    description: 'Livestock panels, a handling race, a creep gate and a poultry run.',
    projectList: [
      'P11 — 10 FT Freestanding Livestock Panel',
      'P12 — Panel Connector Pins & Panel Feet',
      'P13 — Sheep & Goat Handling Race',
      'P14 — Adjustable Calf Creep Gate',
      'P15 — Poultry & Small-Animal Run Frame',
    ],
    src: COVERS.v3,
    alt: 'Volume 03 cover: Pens, Panels & Small-Stock Handling',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 04',
    title: 'Water & Hay Handling',
    projects: '5 projects',
    description: 'Trough stands, bale racks, a bale hand truck and barn carts.',
    projectList: [
      'P16 — Water Trough Stand with Float Guard',
      'P17 — Off-Ground Hay Bale Storage Rack',
      'P18 — Hay Bale Hand Truck',
      'P19 — Heavy-Duty Hose Reel Cart',
      'P20 — Bucket & Feed Bin Rack',
    ],
    src: COVERS.v4,
    alt: 'Volume 04 cover: Water & Hay Handling',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 05',
    title: 'ATV & Yard Utility Builds',
    projects: '5 projects',
    description: 'A drag harrow hitch, an ATV cargo box and fencing gear.',
    projectList: [
      'P21 — ATV Tow-Behind Chain Drag Harrow',
      'P22 — ATV Rear Cargo Box',
      'P23 — Manual T-Post Puller',
      'P24 — Fence Wire Spool Unroller',
      'P25 — Fencing Supply & Tool Cart',
    ],
    src: COVERS.v5,
    alt: 'Volume 05 cover: ATV & Yard Utility Builds',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 06',
    title: 'Barn & Stable Builds',
    projects: '5 projects',
    description: 'Saddle racks, tool racks, feed room stands and a ranch sign.',
    projectList: [
      'P26 — Saddle Rack & Tack Stand',
      'P27 — Barn Tool Wall Rack',
      'P28 — Feed Room Bin Stand',
      'P29 — Boot Scraper & Wash Station',
      'P30 — Hanging Ranch Sign Frame',
    ],
    src: COVERS.v6,
    alt: 'Volume 06 cover: Barn & Stable Builds',
    ...COVER_SIZE,
  },
]

const BONUS_SIZE = { width: 1100, height: 1100 }

export const BONUSES: Bonus[] = [
  {
    number: '01',
    title: 'Steel Purchase & Cut List Planner',
    description: 'Buy the right tube, pipe and bar once, cut it in order and label every part.',
    src: `${IMG}1792795852388_fr-bonus-01-70b37596.webp`,
    alt: 'Bonus 01: Steel Purchase & Cut List Planner (Farm Edition)',
    ...BONUS_SIZE,
  },
  {
    number: '02',
    title: 'Fence & Gate Layout Planner',
    description: 'Measure the opening and place every post, hinge and latch before you cut.',
    src: `${IMG}1792795857606_fr-bonus-02-e5cbf504.webp`,
    alt: 'Bonus 02: Fence & Gate Layout Planner',
    ...BONUS_SIZE,
  },
  {
    number: '03',
    title: 'Weekend Batch-Build Planner',
    description: 'Build three, five or ten panels or feeders in one weekend.',
    src: `${IMG}1792795862930_fr-bonus-03-bdc1b933.webp`,
    alt: 'Bonus 03: Weekend Batch-Build Planner',
    ...BONUS_SIZE,
  },
  {
    number: '04',
    title: 'Local Farm Selling Kit',
    description: 'Listing templates, photo setups and simple quotes for farm buyers.',
    src: `${IMG}1792795867918_fr-bonus-04-48954a71.webp`,
    alt: 'Bonus 04: Local Farm Selling Kit — Listings, Photos & Quotes',
    ...BONUS_SIZE,
  },
  {
    number: '05',
    title: 'Project Costing & Pricing Workbook',
    description: 'Know what each build really costs before you set a price.',
    src: `${IMG}1792795872765_fr-bonus-05-885fdac0.webp`,
    alt: 'Bonus 05: Project Costing & Pricing Workbook',
    ...BONUS_SIZE,
  },
]

export const OFFER_FEATURES: string[] = [
  '3-view dimensioned plans — every measurement in inches and millimeters',
  'Complete cut lists, exploded views and step-by-step fabrication',
  'Weld maps and a Build & Sell card for every project',
]

export const FAQS: Faq[] = [
  {
    question: 'What am I buying?',
    answer:
      'A digital collection of farm and ranch metal project plans: 30 projects across six PDF volumes, plus five bonus resources.',
  },
  {
    question: 'Who is this collection for?',
    answer:
      'Farm, ranch and homestead welders who already know how to weld and want ready-to-build plans for gates, feeders, panels and barn equipment — for their own place or to sell to neighbors.',
  },
  {
    question: 'Is this a beginner welding course?',
    answer:
      'No. The collection gives you the plans: dimensions, cut lists, drawings, build steps and weld maps. It does not teach welding from the beginning.',
  },
  {
    question: 'What kinds of projects are included?',
    answer:
      'Field, walk and corral gates, fence corners and gate hardware, hay and livestock feeders, livestock panels and handling gear, water and hay handling equipment, ATV and fencing tools, and barn and stable builds.',
  },
  {
    question: 'Are the plans in inches or millimeters?',
    answer: 'Both. Every dimension on every project page is printed in inches and millimeters.',
  },
  {
    question: 'Are these physical books or equipment?',
    answer:
      'The product is digital. You receive PDF files by email right after payment; steel, tools and equipment are not included.',
  },
  {
    question: 'Can I preview the plans?',
    answer: 'Yes. All six pages of Project P01 — 12 FT Tube Field Gate, from Volume 01, are in the preview section above.',
  },
  {
    question: 'Can I sell the projects I build from these plans?',
    answer:
      'Yes. Your purchase includes permission to build projects from these plans for your own use or to sell the physical pieces you make. You may print working copies for your own use. You may not share, resell, redistribute or sublicense the digital plans, PDFs, images or bonus files.',
  },
]

export const SUPPORT_EMAIL = 'atencion.creativodigital@gmail.com'

/** Single discreet guarantee line, shown only under the final purchase button. */
export const GUARANTEE_LINE = `7-day guarantee: if you have any problem with your purchase, email ${SUPPORT_EMAIL}.`
